import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { defer, Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AdminBoxUpdateRequestDto,
  Box,
  BoxCreateRequest,
  BoxesStatistics,
  BoxPageRequest,
  BoxStatus,
  BoxUpdateRequest,
  GenerateQRCodeData,
  QrCodeData,
} from '../models';
import { AuthStore, DemoStore } from '../stores';
import { Role } from '../enums';
import { UnregisteredBox } from '../models/unregistered-box';
import { DemoDataService } from './demo-data.service';

@Injectable({ providedIn: 'root' })
export class BoxService {
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly demoDataService = inject(DemoDataService);

  $user = this.authStore.user;

  getCustomerBoxesStatistics(customerId: number | undefined): Observable<BoxesStatistics> {
    if (this.demoStore.isDemoMode()) {
      const boxes = this.demoDataService.getBoxes();
      const connectedCount = boxes.filter((b) => b.connected).length;

      return of({
        connectedCount,
        totalCount: boxes.length,
      });
    }

    return this.httpClient.get<BoxesStatistics>(`/api/customers/${customerId || 'me'}/boxes/statistics`);
  }

  getUserBoxesStatistics(): Observable<BoxesStatistics> {
    if (this.demoStore.isDemoMode()) {
      return this.getCustomerBoxesStatistics(undefined);
    }
    return this.httpClient.get<BoxesStatistics>('/api/customers/me/boxes/statistics');
  }

  getPartnerBoxesStatistics(): Observable<BoxesStatistics> {
    if (this.demoStore.isDemoMode()) {
      return this.getCustomerBoxesStatistics(undefined);
    }
    return this.httpClient.get<BoxesStatistics>('/api/partners/me/boxes/statistics');
  }

  getAdminBoxesStatistics(): Observable<BoxesStatistics> {
    if (this.demoStore.isDemoMode()) {
      return this.getCustomerBoxesStatistics(undefined);
    }
    return this.httpClient.get<BoxesStatistics>('/api/admin/boxes/statistics');
  }

  getUnregisteredBoxes(): Observable<UnregisteredBox[]> {
    if (this.demoStore.isDemoMode()) {
      return of([]);
    }
    return this.httpClient.get<UnregisteredBox[]>('/api/admin/boxes/unregistered');
  }

  registerBox(id: string): Observable<Box> {
    return this.httpClient.post<Box>(`/api/admin/boxes/unregistered/${id}/register`, {});
  }

  deleteBox(id: string, customerId?: number): Observable<void> {
    return this.deleteBoxByRole(id, customerId);
  }

  deleteBoxByRole(id: string, customerId?: number): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      this.demoDataService.deleteBox(id);
      return of(undefined);
    }

    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.deleteAdminBox(id);
      case Role.Partner:
        return this.deletePartnerBox(id);
      default:
        return this.deleteCustomerBox(customerId, id);
    }
  }

  deleteCustomerBox(customerId: number | undefined, id: string): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      this.demoDataService.deleteBox(id);
      return of(undefined);
    }
    return this.httpClient.delete<void>(`/api/customers/${customerId || 'me'}/boxes/${id}`);
  }

  deleteUserBox(id: string): Observable<void> {
    return this.deleteCustomerBox(undefined, id);
  }

  deletePartnerBox(id: string): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      this.demoDataService.deleteBox(id);
      return of(undefined);
    }
    return this.httpClient.delete<void>(`/api/partners/me/boxes/${id}`);
  }

  deleteAdminBox(id: string): Observable<void> {
    if (this.demoStore.isDemoMode()) {
      this.demoDataService.deleteBox(id);
      return of(undefined);
    }
    return this.httpClient.delete<void>(`/api/admin/boxes/${id}`);
  }

  getBoxById(id: string, customerId?: number): Observable<Box> {
    if (this.demoStore.isDemoMode()) {
      const box = this.demoDataService.getBoxes().find((demoBox) => demoBox.id === id);

      if (!box) {
        return throwError(() => new Error(`Demo box with id "${id}" not found`));
      }

      return of(box);
    }

    return this.httpClient
      .get<any>(`/api/customers/${customerId || 'me'}/boxes/${id}`)
      .pipe(map((box) => Box.mapFromApiValue(box)));
  }

  getBoxesStatisticsByRole(): Observable<BoxesStatistics> {
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminBoxesStatistics();
      case Role.Partner:
        return this.getPartnerBoxesStatistics();
      default:
        return this.getUserBoxesStatistics();
    }
  }

  getBoxesByRole(pageRequest: BoxPageRequest): Observable<Box[]> {
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.getAdminBoxes(pageRequest);
      case Role.Partner:
        return this.getPartnerBoxes(pageRequest);
      default:
        return this.getUserBoxes(pageRequest);
    }
  }

  getUserBoxes(pageRequest: BoxPageRequest): Observable<Box[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getBoxes());
    }

    return this.httpClient
      .get<any[]>('/api/customers/me/boxes', { params: BoxPageRequest.mapToQuery(pageRequest) })
      .pipe(map((boxes) => boxes.map((box) => Box.mapFromApiValue(box))));
  }

  getCustomerBoxes(customerId: number | undefined, pageRequest: BoxPageRequest): Observable<Box[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getBoxes());
    }

    return this.httpClient
      .get<any[]>(`/api/customers/${customerId || 'me'}/boxes`, { params: BoxPageRequest.mapToQuery(pageRequest) })
      .pipe(map((boxes) => boxes.map((box) => Box.mapFromApiValue(box))));
  }

  getPartnerBoxes(pageRequest: BoxPageRequest): Observable<Box[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getBoxes());
    }

    return this.httpClient
      .get<any[]>(`/api/partners/me/boxes`, { params: BoxPageRequest.mapToQuery(pageRequest) })
      .pipe(map((boxes) => boxes.map((box) => Box.mapFromApiValue(box))));
  }

  getAdminBoxes(pageRequest: BoxPageRequest): Observable<Box[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getBoxes());
    }

    return this.httpClient
      .get<any[]>(`/api/admin/boxes`, { params: BoxPageRequest.mapToQuery(pageRequest) })
      .pipe(map((boxes) => boxes.map((box) => Box.mapFromApiValue(box))));
  }

  getAllBoxes(customerId?: number): Observable<Box[]> {
    if (this.demoStore.isDemoMode()) {
      return of(this.demoDataService.getBoxes());
    }

    return this.httpClient
      .get<any[]>(`/api/customers/${customerId || 'me'}/boxes/all`)
      .pipe(map((boxes) => boxes.map((box) => Box.mapFromApiValue(box))));
  }

  getBoxStatuses(boxes: string[], customerId: number | undefined): Observable<BoxStatus[]> {
    if (this.demoStore.isDemoMode()) {
      return of(
        this.demoDataService.getBoxes().map((box) => ({
          id: box.id,
          status: box.status,
        })),
      );
    }

    const params: any = { boxes };
    return this.httpClient
      .get<any[]>(`/api/customers/${customerId || 'me'}/boxes/statuses`, {
        params,
      })
      .pipe(map((statuses) => statuses.map((status) => BoxStatus.mapFromApiValue(status))));
  }

  getBoxByQrCode(customerId: number | undefined, qrCodeId: string): Observable<Box> {
    return this.httpClient
      .get<any>(`/api/customers/${customerId || 'me'}/boxes/qr-code/${qrCodeId}`)
      .pipe(map((box) => Box.mapFromApiValue(box)));
  }

  createBox(
    customerId: number | undefined,
    boxData: BoxCreateRequest,
  ): Observable<{
    created: boolean;
    box: Box;
  }> {
    if (this.demoStore.isDemoMode()) {
      return of({
        created: true,
        box: this.demoDataService.getBoxes()[0],
      });
    }

    return this.httpClient
      .post<any>(`/api/customers/${customerId || 'me'}/boxes`, boxData, { observe: 'response' })
      .pipe(
        map((response) => ({
          created: response.status === 201,
          box: Box.mapFromApiValue(response.body),
        })),
      );
  }

  updateBoxByRole(id: string, boxData: BoxUpdateRequest): Observable<Box> {
    switch (this.$user()?.role) {
      case Role.GlobalAdmin:
        return this.updateAdminBox(id, boxData);
      case Role.Partner:
        return this.updatePartnerBox(id, boxData);
      default:
        return this.updateUserBox(id, boxData);
    }
  }

  updateCustomerBox(customerId: number | undefined, id: string, boxData: BoxUpdateRequest): Observable<Box> {
    if (this.demoStore.isDemoMode()) {
      return defer(() => of(this.demoDataService.updateBox(id, boxData)));
    }
    return this.httpClient
      .patch<any>(`/api/customers/${customerId || 'me'}/boxes/${id}`, BoxUpdateRequest.mapToApiValue(boxData))
      .pipe(map((box) => Box.mapFromApiValue(box)));
  }

  updateUserBox(id: string, boxData: BoxUpdateRequest): Observable<Box> {
    if (this.demoStore.isDemoMode()) {
      return defer(() => of(this.demoDataService.updateBox(id, boxData)));
    }
    return this.httpClient
      .patch<any>(`/api/customers/me/boxes/${id}`, BoxUpdateRequest.mapToApiValue(boxData))
      .pipe(map((box) => Box.mapFromApiValue(box)));
  }

  updatePartnerBox(id: string, boxData: BoxUpdateRequest): Observable<Box> {
    if (this.demoStore.isDemoMode()) {
      return defer(() => of(this.demoDataService.updateBox(id, boxData)));
    }
    return this.httpClient
      .patch<any>(`/api/partners/me/boxes/${id}`, BoxUpdateRequest.mapToApiValue(boxData))
      .pipe(map((box) => Box.mapFromApiValue(box)));
  }

  updateAdminBox(id: string, boxData: AdminBoxUpdateRequestDto | BoxUpdateRequest): Observable<Box> {
    if (this.demoStore.isDemoMode()) {
      return defer(() => of(this.demoDataService.updateBox(id, boxData as any)));
    }
    const payload =
      'partnerId' in boxData || 'customerId' in boxData
        ? boxData
        : BoxUpdateRequest.mapToApiValue(boxData as BoxUpdateRequest);
    return this.httpClient.patch<any>(`/api/admin/boxes/${id}`, payload).pipe(map((box) => Box.mapFromApiValue(box)));
  }

  generateQRCode(generateQRCodeData: GenerateQRCodeData): Observable<QrCodeData> {
    if (this.demoStore.isDemoMode()) {
      return of({
        filename: 'qr-code.jpg',
        url: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23eee"/><text x="10" y="50" fill="%23333">QR-CODE</text></svg>',
      });
    }
    return this.httpClient
      .post('/api/admin/boxes/qr-code', generateQRCodeData, { responseType: 'arraybuffer', observe: 'response' })
      .pipe(
        map((response) => {
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') ?? 'application/octet-stream',
          });
          const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] ?? 'qr-code.jpg';
          return {
            filename: filename,
            url: window.URL.createObjectURL(blob),
          };
        }),
      );
  }
}
